import { action, computed, observable, makeAutoObservable } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { FormInputState, FormModel, group, Result, ResultValue } from 'src_common/common/mobx-utils/Form';
import { InputStatus } from 'src/components/Input/InputStatus';
import { validateRequire, validateSpecialCharactersInUrl } from 'src/api/utils/formValidators';
import { apiCommon } from 'src/api/ApiCommon';
import { Response200Type } from 'src/api_openapi/generated/openapi_website_cms_getTopNavigationLinks';
import { TextareaStatus } from 'src/components/FormComponents/Textarea/TextareaStatus';

export type LandingPageContentResponse =
    | HeaderNavigationLinkLoading
    | PromotionLandingPageReady
    | HeaderNavigationLinkError;

interface HeaderNavigationLinkLoading {
    type: 'loading';
}

type HeaderNavigationLinkType = any | null;
interface PromotionLandingPageReady {
    type: 'ready';
    value: Array<HeaderNavigationLinkType>;
}

interface HeaderNavigationLinkError {
    type: 'error';
    value: string;
}

export interface PagesGetType {
    title: string;
    content: string;
    slug: string;
    page_description: string;
    keywords: string;
    seo_title: string;
    page_language: string;
    id?: number;
}

export class PageItem {
    constructor(
        public title: string,
        public content: string,
        public slug: string,
        public page_description: string,
        public keywords: string,
        public seo_title: string,
        public page_language: string,
        public universe: string | null
    ) {}
}

export interface PageType {
    slug: string;
    title: string;
    content: string;
    page_description: string;
    keywords: string;
    seo_title: string;
}

type CurrentPageType = 'edit' | 'add';

export class PagesState {
    @observable readonly pagesResource: Resource<Response200Type | null>;
    @observable readonly universe: string | null;
    @observable page: string = 'add';
    @observable editedPage: PagesGetType | null = null;
    @observable imgErrorMessage: string | null = null;
    @observable isLoading: boolean = false;
    @observable errorMessage: string = '';
    @observable filterLanguage: string = 'en-gb';
    @observable language: string = 'en-gb';

    readonly slugModel: FormModel<string>;
    readonly slug: FormInputState<string>;
    readonly slugStatus: InputStatus;
    readonly titleModel: FormModel<string>;
    readonly title: FormInputState<string>;
    readonly titleStatus: InputStatus;
    readonly contentModel: FormModel<string>;
    readonly content: FormInputState<string>;
    readonly contentStatus: TextareaStatus;
    readonly descriptionModel: FormModel<string>;
    readonly page_description: FormInputState<string>;
    readonly descriptionStatus: InputStatus;
    readonly keywordsModel: FormModel<string>;
    readonly keywords: FormInputState<string>;
    readonly keywordsStatus: InputStatus;
    readonly seoTitleModel: FormModel<string>;
    readonly seo_title: FormInputState<string>;
    readonly seoTitleStatus: InputStatus;

    readonly form: FormModel<PageType>;

    constructor(universe: string | null) {
        this.universe = universe;
        this.pagesResource = new Resource(async (): Promise<Response200Type | null> => {
            this.isLoading = true;
            const response = await apiCommon.getPagesList.run({ language: this.filterLanguage });
            this.isLoading = false;
            //@ts-expect-error
            return response;
        });

        this.slug = new FormInputState('', false);
        this.slugModel = this.slug.map(validateRequire).map(validateSpecialCharactersInUrl);
        this.slugStatus = new InputStatus(this.slugModel);

        this.title = new FormInputState('', false);
        this.titleModel = this.title.map(validateRequire);
        this.titleStatus = new InputStatus(this.titleModel);

        this.content = new FormInputState('', false);
        this.contentModel = this.content.toModel();
        this.contentStatus = new TextareaStatus(this.contentModel);

        this.page_description = new FormInputState('', false);
        this.descriptionModel = this.page_description.toModel();
        this.descriptionStatus = new InputStatus(this.descriptionModel);

        this.keywords = new FormInputState('', false);
        this.keywordsModel = this.keywords.toModel();
        this.keywordsStatus = new InputStatus(this.keywordsModel);

        this.seo_title = new FormInputState('', false);
        this.seoTitleModel = this.seo_title.toModel();
        this.seoTitleStatus = new InputStatus(this.seoTitleModel);

        this.form = group({
            slug: this.slugModel,
            title: this.titleModel,
            content: this.contentModel,
            page_description: this.descriptionModel,
            keywords: this.keywordsModel,
            seo_title: this.seoTitleModel,
        }).map((item: PageType): Result<PageType> => {
            return new ResultValue({
                slug: item.slug,
                title: item.title,
                content: item.content,
                page_description: item.page_description,
                keywords: item.keywords,
                seo_title: item.seo_title,
            });
        });
        makeAutoObservable(this);
    }

    @computed get allPages(): LandingPageContentResponse {
        const pages = this.pagesResource.get();
        if (pages.type === 'ready' && pages.value !== null) {
            return {
                type: 'ready',
                value: pages.value,
            };
        }
        if (pages.type === 'loading') {
            return {
                type: 'loading',
            };
        }

        return {
            type: 'error',
            value: 'Something went wrong with fetching landing page info',
        };
    }

    @computed get isEditMode(): boolean {
        return this.page === 'edit';
    }

    @action handleCancel = (): void => {
        if (this.isEditMode && this.editedPage !== null) {
            this.setEditedPage(this.editedPage);
        } else {
            this.form.reset();
        }
    };

    @action setEditedPage = (editedPage: PagesGetType): void => {
        this.editedPage = editedPage;
        if (editedPage !== null && editedPage !== undefined) {
            this.title.setValue(editedPage.title);
            this.content.setValue(editedPage.content);
            this.slug.setValue(editedPage.slug);
            this.page_description.setValue(editedPage.page_description);
            this.keywords.setValue(editedPage.keywords);
            this.seo_title.setValue(editedPage.seo_title);
            this.language = editedPage.page_language;
        }
    };

    @action setFilterLanguage = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const lang = event.target.value;
        this.filterLanguage = lang;
        this.pagesResource.refresh();
    };

    @action setLanguage = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const lang = event.target.value;
        this.language = lang;
    };

    @computed get isFormValid(): boolean {
        return this.form.result instanceof ResultValue;
    }

    @computed get allPagesSorted(): Array<PagesGetType> | null {
        if (this.allPages.type === 'ready') {
            return this.allPages.value.sort((page1: PagesGetType, page2: PagesGetType) =>
                page1 !== null && page2 !== null ? page1.title.localeCompare(page2.title) : -1
            );
        }
        return [];
    }

    @action setDefaultState = (): void => {
        this.setPage('add');
        this.editedPage = null;
        this.form.reset();
    };

    @action onRemove = async (element: PagesGetType) => {
        const removeResult = element.id !== undefined ? await apiCommon.deletePage.run({ id: element.id }) : null;

        if (removeResult) {
            await this.pagesResource.refresh();

            this.form.reset();
        } else {
            this.errorMessage = 'Unknown error. Something went wrong.';
        }
    };

    @action submitPage = async (page: PageType): Promise<number | Error | null> => {
        let response: number | Error | null;
        if (this.universe !== null) {
            try {
                const pageBody = {
                    page: {
                        slug: page.slug,
                        title: page.title,
                        content: page.content,
                        page_description: page.page_description,
                        keywords: page.keywords,
                        seo_title: page.seo_title,
                        page_language: this.language,
                        universe: this.universe,
                    },
                };
                response = await apiCommon.postPage.run(pageBody);
            } catch (error) {
                console.error(error);
                return null;
            }
            return response;
        }
        return null;
    };

    @action submitEditedPage = async (page: PagesGetType, id: number): Promise<number | Error | null> => {
        let response: number | Error | null;
        if (this.universe !== null) {
            try {
                const pageObject = {
                    page: {
                        slug: page.slug,
                        title: page.title,
                        content: page.content,
                        page_description: page.page_description,
                        keywords: page.keywords,
                        seo_title: page.seo_title,
                        universe: this.universe,
                        page_language: this.language,
                    },
                    id: id,
                };
                response = await apiCommon.putPage.run(pageObject);
            } catch (error) {
                console.error(error);
                return null;
            }
            return response;
        }

        return null;
    };

    @action handleSubmit = async () => {
        this.isLoading = true;
        this.errorMessage = '';
        this.form.setAsVisited();
        const result = this.form.result;

        if (result instanceof ResultValue) {
            const payload: PageItem = {
                title: result.value.title,
                page_description: result.value.page_description,
                slug: result.value.slug,
                seo_title: result.value.seo_title,
                keywords: result.value.keywords,
                content: result.value.content,
                page_language: this.language,
                universe: this.universe,
            };

            const submitResult =
                this.editedPage && this.editedPage.id !== undefined
                    ? await this.submitEditedPage(payload, this.editedPage.id)
                    : await this.submitPage(payload);
            if (submitResult) {
                await this.pagesResource.refresh();
                this.setDefaultState();
            } else {
                this.errorMessage = 'Unknown error. Something went wrong.';
            }
        }

        this.isLoading = false;
    };

    @action setPage = (value: CurrentPageType): void => {
        this.page = value;
    };

    static createForContext(): PagesState {
        return new PagesState('star');
    }
}
